/**
 * Frontend RBAC Permissions System
 * 
 * This is a copy of the shared permissions system specifically for frontend use.
 * It provides the same RBAC functionality without external package dependencies.
 */

// Role definitions
const TENANT_ADMINISTRATOR = 'tenant_administrator';
const ORGANIZATION_ADMIN = 'organization_admin';
const ORGANIZATION_TEACHER = 'organization_teacher';
const ORGANIZATION_STAFF = 'organization_staff';
const SCHOOL_ADMIN = 'school_admin';
const TEACHER = 'teacher';
// Role constants kept in PERMISSION_MATRIX usage; no direct export needed

// Permission Actions - Standard CRUD + Special Actions
export const ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  INVITE: 'invite',
  REMOVE: 'remove',
  TAKE_ATTENDANCE: 'take_attendance',
  VIEW_REPORTS: 'view_reports',
  MANAGE_SCHOOL: 'manage_school',
  MANAGE_ORGANIZATION: 'manage_organization',
  ENROLL_STUDENT: 'enroll_student',
  UNENROLL_STUDENT: 'unenroll_student'
};

// Resources in the System
export const RESOURCES = {
  ORGANIZATION: 'organization',
  SCHOOL: 'school',
  USER: 'user',
  STUDENT: 'student',
  CLASSROOM: 'classroom',
  SUBJECT: 'subject',
  PERIOD: 'period',
  ATTENDANCE: 'attendance',
  CATEGORY: 'category',
  REPORTS: 'reports',
  INVITATIONS: 'invitations'
};

// Permission Scopes
export const SCOPES = {
  ORGANIZATION: 'organization', // Can access across entire organization
  SCHOOL: 'school',            // Can only access within assigned schools
  OWN: 'own',                   // Can only access own data
  ASSIGNED_CLASSES: 'assigned_classes' // Can only access classes where user is assigned as instructor
};

/**
 * Permission Matrix - Defines what each role can do
 * Format: [resource]: { [action]: scope }
 */
export const PERMISSION_MATRIX = {
  [TENANT_ADMINISTRATOR]: {
    // Full access to everything
    [RESOURCES.ORGANIZATION]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.MANAGE_ORGANIZATION]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SCHOOL]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.MANAGE_SCHOOL]: SCOPES.ORGANIZATION
    },
    [RESOURCES.USER]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.INVITE]: SCOPES.ORGANIZATION,
      [ACTIONS.REMOVE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.STUDENT]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CLASSROOM]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SUBJECT]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.PERIOD]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.ATTENDANCE]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.TAKE_ATTENDANCE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CATEGORY]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.REPORTS]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.VIEW_REPORTS]: SCOPES.ORGANIZATION
    },
    [RESOURCES.INVITATIONS]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    }
  },

  [ORGANIZATION_ADMIN]: {
    // Cannot delete organization or manage other org admins/tenant admins
    [RESOURCES.ORGANIZATION]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SCHOOL]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.MANAGE_SCHOOL]: SCOPES.ORGANIZATION
    },
    [RESOURCES.USER]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.INVITE]: SCOPES.ORGANIZATION,
      [ACTIONS.REMOVE]: SCOPES.ORGANIZATION // Limited by role hierarchy
    },
    [RESOURCES.STUDENT]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CLASSROOM]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SUBJECT]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.PERIOD]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.ATTENDANCE]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.TAKE_ATTENDANCE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CATEGORY]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.REPORTS]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.VIEW_REPORTS]: SCOPES.ORGANIZATION
    },
    [RESOURCES.INVITATIONS]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    }
  },

  [ORGANIZATION_TEACHER]: {
    // Read/Write/Delete/Update in every school, cannot invite
    [RESOURCES.ORGANIZATION]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SCHOOL]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.USER]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.STUDENT]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CLASSROOM]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SUBJECT]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.PERIOD]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.ATTENDANCE]: {
      [ACTIONS.CREATE]: SCOPES.ORGANIZATION,
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.UPDATE]: SCOPES.ORGANIZATION,
      [ACTIONS.DELETE]: SCOPES.ORGANIZATION,
      [ACTIONS.TAKE_ATTENDANCE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CATEGORY]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.REPORTS]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.VIEW_REPORTS]: SCOPES.ORGANIZATION
    }
  },

  [ORGANIZATION_STAFF]: {
    // Read-only access across every school, can take attendance
    [RESOURCES.ORGANIZATION]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SCHOOL]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.USER]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.STUDENT]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CLASSROOM]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.SUBJECT]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.PERIOD]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.ATTENDANCE]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.TAKE_ATTENDANCE]: SCOPES.ORGANIZATION
    },
    [RESOURCES.CATEGORY]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION
    },
    [RESOURCES.REPORTS]: {
      [ACTIONS.READ]: SCOPES.ORGANIZATION,
      [ACTIONS.VIEW_REPORTS]: SCOPES.ORGANIZATION
    }
  },

  [SCHOOL_ADMIN]: {
    // Full access within assigned schools, can invite teachers and support staff
    [RESOURCES.ORGANIZATION]: {
      [ACTIONS.READ]: SCOPES.SCHOOL
    },
    [RESOURCES.SCHOOL]: {
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL
    },
    [RESOURCES.USER]: {
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.REMOVE]: SCOPES.SCHOOL
    },
    [RESOURCES.STUDENT]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    },
    [RESOURCES.CLASSROOM]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    },
    [RESOURCES.SUBJECT]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    },
    [RESOURCES.PERIOD]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    },
    [RESOURCES.ATTENDANCE]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL,
      [ACTIONS.TAKE_ATTENDANCE]: SCOPES.SCHOOL
    },
    [RESOURCES.CATEGORY]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    },
    [RESOURCES.REPORTS]: {
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.VIEW_REPORTS]: SCOPES.SCHOOL
    },
    [RESOURCES.INVITATIONS]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    }
  },

  [TEACHER]: {
    // School-specific access only
    [RESOURCES.ORGANIZATION]: {
      [ACTIONS.READ]: SCOPES.SCHOOL
    },
    [RESOURCES.SCHOOL]: {
      [ACTIONS.READ]: SCOPES.SCHOOL
    },
    [RESOURCES.USER]: {
      [ACTIONS.READ]: SCOPES.SCHOOL
    },
    [RESOURCES.STUDENT]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.ENROLL_STUDENT]: SCOPES.ASSIGNED_CLASSES,
      [ACTIONS.UNENROLL_STUDENT]: SCOPES.ASSIGNED_CLASSES
    },
    [RESOURCES.CLASSROOM]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.ASSIGNED_CLASSES
    },
    [RESOURCES.SUBJECT]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.ASSIGNED_CLASSES
    },
    [RESOURCES.PERIOD]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.SCHOOL,
      [ACTIONS.DELETE]: SCOPES.SCHOOL
    },
    [RESOURCES.ATTENDANCE]: {
      [ACTIONS.CREATE]: SCOPES.ASSIGNED_CLASSES,
      [ACTIONS.READ]: SCOPES.ASSIGNED_CLASSES,
      [ACTIONS.UPDATE]: SCOPES.ASSIGNED_CLASSES,
      [ACTIONS.TAKE_ATTENDANCE]: SCOPES.ASSIGNED_CLASSES
    },
    [RESOURCES.CATEGORY]: {
      [ACTIONS.CREATE]: SCOPES.SCHOOL,
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.UPDATE]: SCOPES.ASSIGNED_CLASSES
    },
    [RESOURCES.REPORTS]: {
      [ACTIONS.READ]: SCOPES.SCHOOL,
      [ACTIONS.VIEW_REPORTS]: SCOPES.SCHOOL
    }
  }
};

/**
 * Check if a user has permission to perform an action on a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource being accessed
 * @param {string} action - The action being performed
 * @param {Object} context - Additional context (schoolId, organization_id, etc.)
 * @returns {boolean} - Whether the user has permission
 */
export function hasPermission(userRole, resource, action, context = {}) {
  const rolePermissions = PERMISSION_MATRIX[userRole];
  if (!rolePermissions) return false;

  const resourcePermissions = rolePermissions[resource];
  if (!resourcePermissions) return false;

  const actionScope = resourcePermissions[action];
  if (!actionScope) return false;

  // Permission exists, now check scope
  return checkScope(userRole, actionScope, context);
}

/**
 * Check if the user's scope allows access to the requested resource
 * @param {string} userRole - The user's role
 * @param {string} permissionScope - The scope required for the permission
 * @param {Object} context - Context including user's accessible schools, organization, etc.
 * @returns {boolean} - Whether scope allows access
 */
export function checkScope(userRole, permissionScope, context = {}) {
  const { 
    userAccessibleSchoolIds = [], 
    requestedSchoolId, 
    userId, 
    targetUserId,
    userAssignedClassIds = [],
    requestedClassId,
    resourceClassId,
    isAdminView = false
  } = context;

  // Special handling for admin_view contexts
  if (isAdminView && (userRole === 'teacher' || userRole === 'substitute' || userRole === 'organization_teacher')) {
    if (permissionScope === SCOPES.ASSIGNED_CLASSES) {
      permissionScope = SCOPES.SCHOOL;
    }
  }

  switch (permissionScope) {
    case SCOPES.ORGANIZATION:
      // User can access anything in the organization
      return true;

    case SCOPES.SCHOOL:
      // User can only access resources in their assigned schools
      // Empty accessibleSchoolIds means access to all schools in organization
      if (userAccessibleSchoolIds.length === 0) {
        return true; // Access to all schools in organization
      }
      
      if (!requestedSchoolId) {
        // If no specific school requested, user needs to have some school access
        return userAccessibleSchoolIds.length > 0;
      }
      
      // Check if user has access to the specific school
      return userAccessibleSchoolIds.includes(requestedSchoolId);

    case SCOPES.ASSIGNED_CLASSES:
      // User can only access resources for classes where they are assigned as instructor
      const classIdToCheck = requestedClassId || resourceClassId;
      
      if (!classIdToCheck) {
        // If no specific class requested, user needs to have some assigned classes
        return userAssignedClassIds.length > 0;
      }
      
      // Check if user is assigned to the specific class
      return userAssignedClassIds.includes(classIdToCheck);

    case SCOPES.OWN:
      // User can only access their own data
      return userId === targetUserId;

    default:
      return false;
  }
}